import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { 
  Menu, 
  X, 
  ChevronDown, 
  User, 
  Settings, 
  LogOut, 
  CreditCard
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { useChat } from '../../context/ChatContext';
import Button from '../ui/Button';

const Navbar = () => {
  const { user, signOut } = useAuth();
  const { getRemainingCalcs, isPro } = useChat();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [materialsMenuOpen, setMaterialsMenuOpen] = useState(false);

  const materials = [
    { name: 'Concrete', path: '/concrete-calculator', icon: '🧱' },
    { name: 'Drywall', path: '/drywall-calculator', icon: '📏' },
    { name: 'Paint', path: '/paint-calculator', icon: '🎨' },
    { name: 'Flooring', path: '/flooring-calculator', icon: '🏠' },
    { name: 'Lumber', path: '/lumber-calculator', icon: '🪵' },
    { name: 'Roofing', path: '/roofing-calculator', icon: '🏗️' },
  ];

  const handleSignOut = async () => {
    await signOut();
    setUserMenuOpen(false);
    setMobileMenuOpen(false);
    navigate('/');
  };

  const remainingCalcs = getRemainingCalcs();

  return (
    <nav className="bg-white shadow-sm sticky top-0 z-40">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between h-16">
          {/* Logo and main nav */}
          <div className="flex items-center">
            <Link to="/" className="flex items-center gap-2">
              <div className="w-8 h-8 bg-gradient-to-br from-blue-600 to-indigo-600 rounded-lg flex items-center justify-center">
                <span className="text-white font-bold text-sm">CB</span>
              </div>
              <span className="font-bold text-xl text-gray-900 hidden sm:block">
                Calc<span className="text-blue-600">Build</span>AI
              </span>
            </Link>

            {/* Desktop navigation */}
            <div className="hidden md:flex items-center ml-8 space-x-1">
              {/* Materials dropdown */}
              <div className="relative">
                <button
                  onClick={() => setMaterialsMenuOpen(!materialsMenuOpen)}
                  onBlur={() => setTimeout(() => setMaterialsMenuOpen(false), 150)}
                  className="flex items-center gap-1 px-3 py-2 text-gray-600 hover:text-gray-900 hover:bg-gray-50 rounded-lg transition-colors"
                >
                  Calculators
                  <ChevronDown className={`w-4 h-4 transition-transform ${materialsMenuOpen ? 'rotate-180' : ''}`} />
                </button>
                
                {materialsMenuOpen && (
                  <div className="absolute top-full left-0 mt-1 w-56 bg-white rounded-lg shadow-lg border py-2 z-50">
                    {materials.map((material) => (
                      <Link
                        key={material.path}
                        to={material.path}
                        className="flex items-center gap-3 px-4 py-2 text-gray-700 hover:bg-gray-50"
                        onClick={() => setMaterialsMenuOpen(false)}
                      >
                        <span className="text-xl">{material.icon}</span>
                        <span>{material.name} Calculator</span>
                      </Link>
                    ))}
                  </div>
                )}
              </div>

              <Link
                to="/pricing"
                className={`px-3 py-2 rounded-lg transition-colors ${
                  location.pathname === '/pricing' 
                    ? 'text-blue-600 bg-blue-50' 
                    : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                }`}
              >
                Pricing
              </Link>

              <Link
                to="/about"
                className={`px-3 py-2 rounded-lg transition-colors ${
                  location.pathname === '/about' 
                    ? 'text-blue-600 bg-blue-50' 
                    : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                }`}
              >
                About
              </Link>
            </div>
          </div>

          {/* Right side */}
          <div className="flex items-center gap-4">
            {/* Usage indicator */}
            {user && !isPro && (
              <div className="hidden sm:flex items-center gap-2 px-3 py-1.5 bg-blue-50 text-blue-700 rounded-full text-sm">
                <span className="font-medium">{remainingCalcs}/10</span>
                <span className="text-blue-500">calcs</span>
              </div>
            )}
            
            {user && isPro && (
              <div className="hidden sm:flex items-center gap-1 px-3 py-1.5 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-full text-sm font-medium">
                ✨ Pro
              </div>
            )}

            {user ? (
              <div className="relative">
                <button
                  onClick={() => setUserMenuOpen(!userMenuOpen)}
                  onBlur={() => setTimeout(() => setUserMenuOpen(false), 150)}
                  className="flex items-center gap-2 text-gray-700 hover:text-gray-900"
                >
                  <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                    <User className="w-5 h-5 text-blue-600" />
                  </div>
                  <ChevronDown className={`w-4 h-4 hidden sm:block transition-transform ${userMenuOpen ? 'rotate-180' : ''}`} />
                </button>

                {userMenuOpen && (
                  <div className="absolute top-full right-0 mt-2 w-56 bg-white rounded-lg shadow-lg border py-2 z-50">
                    <div className="px-4 py-2 border-b">
                      <p className="text-sm font-medium text-gray-900 truncate">
                        {user.email}
                      </p>
                      <p className="text-xs text-gray-500">
                        {isPro ? 'Pro Member' : 'Free Plan'}
                      </p>
                    </div>
                    
                    <Link
                      to="/settings"
                      className="flex items-center gap-3 px-4 py-2 text-gray-700 hover:bg-gray-50"
                      onClick={() => setUserMenuOpen(false)}
                    >
                      <Settings className="w-4 h-4" />
                      Settings
                    </Link>
                    
                    {!isPro && (
                      <Link
                        to="/pricing"
                        className="flex items-center gap-3 px-4 py-2 text-blue-600 hover:bg-blue-50"
                        onClick={() => setUserMenuOpen(false)}
                      >
                        <CreditCard className="w-4 h-4" />
                        Upgrade to Pro
                      </Link>
                    )}
                    
                    <hr className="my-2" />
                    
                    <button
                      onClick={handleSignOut}
                      className="flex items-center gap-3 w-full px-4 py-2 text-gray-700 hover:bg-gray-50"
                    >
                      <LogOut className="w-4 h-4" />
                      Sign Out
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <div className="hidden sm:flex items-center gap-3">
                <Link to="/signin">
                  <Button variant="ghost">Sign In</Button>
                </Link>
                <Link to="/signup">
                  <Button variant="primary">Sign Up Free</Button>
                </Link>
              </div>
            )}

            {/* Mobile menu button */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="md:hidden p-2 text-gray-700 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/30"
              aria-label={mobileMenuOpen ? 'Close menu' : 'Open menu'}
              aria-expanded={mobileMenuOpen}
            >
              {mobileMenuOpen ? (
                <X className="w-6 h-6" />
              ) : (
                <Menu className="w-6 h-6" />
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile menu */}
      {mobileMenuOpen && (
        <div className="md:hidden border-t bg-white">
          <div className="px-4 py-4 space-y-2">
            <div className="text-xs font-semibold text-gray-400 uppercase px-3 py-2">
              Calculators
            </div>
            {materials.map((material) => (
              <Link
                key={material.path}
                to={material.path}
                className="flex items-center gap-3 px-3 py-2 text-gray-700 hover:bg-gray-50 rounded-lg"
                onClick={() => setMobileMenuOpen(false)}
              >
                <span className="text-xl">{material.icon}</span>
                <span>{material.name}</span>
              </Link>
            ))}
            
            <hr className="my-4" />
            
            <Link
              to="/pricing"
              className="block px-3 py-2 text-gray-700 hover:bg-gray-50 rounded-lg"
              onClick={() => setMobileMenuOpen(false)}
            >
              Pricing
            </Link>
            
            <Link
              to="/about"
              className="block px-3 py-2 text-gray-700 hover:bg-gray-50 rounded-lg"
              onClick={() => setMobileMenuOpen(false)}
            >
              About
            </Link>

            {!user && (
              <>
                <hr className="my-4" />
                <div className="space-y-2">
                  <Link to="/signin" onClick={() => setMobileMenuOpen(false)}>
                    <Button variant="outline" fullWidth>Sign In</Button>
                  </Link>
                  <Link to="/signup" onClick={() => setMobileMenuOpen(false)}>
                    <Button variant="primary" fullWidth>Sign Up Free</Button>
                  </Link>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navbar;
